(() => {
  const DIRECTORY_SELECTOR = '[data-bundle-progressive]';
  const CARD_SELECTOR = '[data-bundle-card]';

  const toNumber = (value, fallback = 0) => {
    const parsed = Number.parseInt(value || '', 10);
    return Number.isNaN(parsed) ? fallback : parsed;
  };

  const getCards = (grid) => Array.from(grid.querySelectorAll(`:scope > ${CARD_SELECTOR}`));

  const decodeHtmlEntities = (value) => {
    let decoded = value;

    for (let index = 0; index < 2; index += 1) {
      const textarea = document.createElement('textarea');
      textarea.innerHTML = decoded;
      const nextValue = textarea.value;
      if (nextValue === decoded) break;
      decoded = nextValue;
    }

    return decoded;
  };

  const sortCards = (cards) => {
    cards.sort((left, right) => {
      const leftHasImage = left.dataset.bundleHasImage === 'true';
      const rightHasImage = right.dataset.bundleHasImage === 'true';

      if (leftHasImage !== rightHasImage) return leftHasImage ? -1 : 1;

      return toNumber(left.dataset.bundleSortOrder) - toNumber(right.dataset.bundleSortOrder);
    });
  };

  document.querySelectorAll(DIRECTORY_SELECTOR).forEach((root) => {
    const grid = root.querySelector('[data-bundle-grid]');
    const pagination = root.querySelector('[data-bundle-pagination]');
    const themeFilter = root.querySelector('[data-bundle-theme-filter]');
    const celebrationFilter = root.querySelector('[data-bundle-celebration-filter]');
    const noResults = root.querySelector('[data-bundle-no-results]');
    const progressBar = root.querySelector('[data-bundle-progress-bar]');
    const progressText = root.querySelector('[data-bundle-progress-text]');
    const section = root.closest('.shopify-section');

    if (!(grid instanceof HTMLElement) || !(pagination instanceof HTMLElement) || !(section instanceof HTMLElement)) return;

    const pageSize = toNumber(pagination.dataset.pageSize, 24);
    const totalCount = toNumber(pagination.dataset.totalCount);
    const sectionId = section.id.replace(/^shopify-section-/, '');
    let visibleLimit = pageSize;
    let isIndexComplete = false;
    let isLoading = false;
    let indexPromise = null;

    const getLoadMore = () => root.querySelector('[data-bundle-load-more]');
    const loadMoreTemplate = getLoadMore()?.cloneNode(true);
    const getCardsInGrid = () => getCards(grid);

    const setBusy = (busy) => {
      isLoading = busy;
      root.setAttribute('aria-busy', String(busy));
      [themeFilter, celebrationFilter].forEach((filter) => {
        if (filter instanceof HTMLSelectElement) filter.disabled = busy;
      });

      const loadMore = getLoadMore();
      if (loadMore instanceof HTMLElement) {
        loadMore.setAttribute('aria-disabled', String(busy));
        loadMore.classList.toggle('is-disabled', busy);
      }
    };

    const updateProgress = (visibleCount, resultCount) => {
      const total = isIndexComplete ? resultCount : totalCount;
      const percentage = total > 0 ? (visibleCount / total) * 100 : 0;
      const template = decodeHtmlEntities(pagination.dataset.progressTemplate || '__items__ / __total__');

      if (progressBar instanceof HTMLElement) {
        progressBar.style.width = `${percentage}%`;
        progressBar.setAttribute('aria-valuemax', String(total));
        progressBar.setAttribute('aria-valuenow', String(visibleCount));
      }

      if (progressText instanceof HTMLElement) {
        progressText.textContent = template.replace('__items__', String(visibleCount)).replace('__total__', String(total));
      }
    };

    const getMatches = () => {
      const selectedTheme = themeFilter instanceof HTMLSelectElement ? themeFilter.value : '';
      const selectedCelebration = celebrationFilter instanceof HTMLSelectElement ? celebrationFilter.value : '';

      return getCardsInGrid().filter((card) => {
        const matchesTheme = selectedTheme === '' || (card.dataset.bundleThemes || '').split('||').includes(selectedTheme);
        const matchesCelebration =
          selectedCelebration === '' || (card.dataset.bundleCelebrations || '').split('||').includes(selectedCelebration);

        return matchesTheme && matchesCelebration;
      });
    };

    const applyFilters = ({ reset = false } = {}) => {
      if (reset) visibleLimit = pageSize;

      const cards = getCardsInGrid();
      const matches = getMatches();
      const matchingCards = new Set(matches);

      cards.forEach((card) => {
        card.classList.toggle('bundle-directory__item--filtered-out', !matchingCards.has(card));
      });

      matches.forEach((card, index) => {
        card.classList.toggle('bundle-directory__item--page-hidden', index >= visibleLimit);
      });

      const visibleCount = Math.min(visibleLimit, matches.length);
      updateProgress(visibleCount, isIndexComplete ? matches.length : totalCount);

      if (noResults instanceof HTMLElement) noResults.classList.toggle('hidden', matches.length > 0);

      const loadMore = getLoadMore();
      if (loadMore instanceof HTMLElement && isIndexComplete) loadMore.hidden = visibleCount >= matches.length;

      return matches;
    };

    const rebuildFilterOptions = () => {
      const filters = [
        [themeFilter, 'bundleThemePairs'],
        [celebrationFilter, 'bundleCelebrationPairs'],
      ];

      filters.forEach(([filter, datasetKey]) => {
        if (!(filter instanceof HTMLSelectElement)) return;

        const selectedValue = filter.value;
        const initialOption = filter.options[0]?.cloneNode(true);
        const options = new Map();

        getCardsInGrid().forEach((card) => {
          const pairs = (card.dataset[datasetKey] || '').split('||');
          const cardHandles = new Set();

          pairs.forEach((pair) => {
            const separator = pair.indexOf('::');
            if (separator === -1) return;

            const handle = pair.slice(0, separator);
            const label = pair.slice(separator + 2);
            if (!handle || !label || cardHandles.has(handle)) return;

            cardHandles.add(handle);
            const option = options.get(handle) || { label, count: 0 };
            option.count += 1;
            options.set(handle, option);
          });
        });

        filter.replaceChildren();
        if (initialOption instanceof HTMLOptionElement) filter.append(initialOption);

        Array.from(options.entries())
          .sort((left, right) => left[1].label.localeCompare(right[1].label))
          .forEach(([value, optionData]) => {
            const option = new Option(`${optionData.label} (${optionData.count})`, value);
            filter.append(option);
          });

        filter.value = selectedValue;
      });
    };

    const getNextUrl = () => {
      const loadMore = getLoadMore();
      return loadMore instanceof HTMLAnchorElement ? loadMore.href : null;
    };

    const updateLoadMore = (sourceRoot) => {
      const nextLink = sourceRoot.querySelector('[data-bundle-load-more]');
      const currentLink = getLoadMore();

      pagination.dataset.currentPage = sourceRoot.querySelector('[data-bundle-pagination]')?.dataset.currentPage || '';

      if (!(nextLink instanceof HTMLAnchorElement)) {
        currentLink?.remove();
        return;
      }

      if (currentLink instanceof HTMLAnchorElement) {
        currentLink.href = nextLink.href;
        currentLink.hidden = false;
        return;
      }

      pagination.append(nextLink.cloneNode(true));
    };

    const ensureClientLoadMore = () => {
      if (getLoadMore() || !(loadMoreTemplate instanceof HTMLElement)) return;

      const button = document.createElement('button');
      button.type = 'button';
      button.className = loadMoreTemplate.className;
      button.textContent = loadMoreTemplate.textContent;
      const controls = loadMoreTemplate.getAttribute('aria-controls');
      if (controls) button.setAttribute('aria-controls', controls);
      button.dataset.bundleLoadMore = '';
      pagination.append(button);
    };

    const loadNextPage = async () => {
      const nextUrl = getNextUrl();
      if (!nextUrl || isLoading) return [];

      setBusy(true);

      try {
        const url = new URL(nextUrl, window.location.origin);
        url.searchParams.set('section_id', sectionId);

        const response = await fetch(url.toString(), { headers: { Accept: 'text/html' } });
        if (!response.ok) throw new Error(`Bundle page request failed: ${response.status}`);

        const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
        const sourceRoot = documentFragment.querySelector(DIRECTORY_SELECTOR);
        const sourceGrid = sourceRoot?.querySelector('[data-bundle-grid]');
        if (!(sourceRoot instanceof HTMLElement) || !(sourceGrid instanceof HTMLElement)) {
          throw new Error('Bundle page response did not include the directory grid');
        }

        const cards = getCards(sourceGrid);
        grid.append(...cards);
        updateLoadMore(sourceRoot);
        return cards;
      } catch (error) {
        console.error('Unable to load additional bundle cards:', error);
        return [];
      } finally {
        setBusy(false);
      }
    };

    const ensureCompleteIndex = async () => {
      if (isIndexComplete) return;
      if (indexPromise) return indexPromise;

      indexPromise = (async () => {
        while (getNextUrl()) {
          const previousUrl = getNextUrl();
          await loadNextPage();
          if (getNextUrl() === previousUrl) break;
        }

        isIndexComplete = !getNextUrl();
        sortCards(getCardsInGrid());
        getCardsInGrid().forEach((card) => grid.append(card));
        rebuildFilterOptions();
        if (isIndexComplete) ensureClientLoadMore();
      })().finally(() => {
        indexPromise = null;
      });

      return indexPromise;
    };

    const prepareFilter = async () => {
      if (!isIndexComplete) {
        await ensureCompleteIndex();
      }

      if (!isIndexComplete) return;
      applyFilters({ reset: true });
    };

    sortCards(getCardsInGrid());
    getCardsInGrid().forEach((card) => grid.append(card));
    rebuildFilterOptions();
    updateProgress(getCardsInGrid().length, totalCount);

    [themeFilter, celebrationFilter].forEach((filter) => {
      if (!(filter instanceof HTMLSelectElement)) return;

      filter.addEventListener('focus', () => {
        void ensureCompleteIndex();
      }, { once: true });
      filter.addEventListener('change', () => {
        void prepareFilter();
      });
    });

    root.addEventListener('click', async (event) => {
      const loadMore = event.target instanceof Element ? event.target.closest('[data-bundle-load-more]') : null;
      if (!(loadMore instanceof HTMLElement)) return;

      event.preventDefault();
      if (isLoading || indexPromise) return;

      if (isIndexComplete) {
        const previousVisible = visibleLimit;
        visibleLimit += pageSize;
        const matches = applyFilters();
        matches[previousVisible]?.querySelector('a[href]')?.focus({ preventScroll: true });
        return;
      }

      if (!(loadMore instanceof HTMLAnchorElement)) return;

      const loadedUrl = loadMore.href;
      const cards = await loadNextPage();
      if (!cards.length && getNextUrl() === loadedUrl) return;

      sortCards(getCardsInGrid());
      getCardsInGrid().forEach((card) => grid.append(card));
      rebuildFilterOptions();
      updateProgress(getCardsInGrid().length, totalCount);
      history.replaceState({}, '', loadedUrl);
      cards[0]?.querySelector('a[href]')?.focus({ preventScroll: true });
    }, true);
  });
})();
